import React, { useEffect } from 'react';
import { AccordionGroupProvider } from '../../components/edit-object/AccordionGroupContext';
import { useUpload } from '../../hooks/useUpload';
import { useArtworkDetailsForm } from './artwork-details/useArtworkDetailsForm';
import { CreatorsSection } from './artwork-details/sections/CreatorsSection';
import { TitleSection } from './artwork-details/sections/TitleSection';
import { MediaDescriptionSection } from './artwork-details/sections/MediaDescriptionSection';
import { SignatureSection } from './artwork-details/sections/SignatureSection';
import { CreationDateSection } from './artwork-details/sections/CreationDateSection';
import { MeasurementsSection } from './artwork-details/sections/MeasurementsSection';
import { KeywordsSection } from './artwork-details/sections/KeywordsSection';
import { MultiplesEditionsSection } from './artwork-details/sections/MultiplesEditionsSection';
import { AdditionalNotesSection } from './artwork-details/sections/AdditionalNotesSection';

interface DescribeStepProps {
  catalogItemId?: string;
  ownerId?: string | null;
  /**
   * Explicit create-vs-edit signal forwarded from EditObject (GH#1225).
   * When false (create wizard), a filename-derived title suggestion set by
   * UploadImagesStep/UploadTab on the first image upload is applied to an
   * empty Title field. When true (edit mode) the suggestion is never
   * applied — mirrors the legacy `wizard/DescribeTab`'s `isEditMode` gate.
   */
  isEditMode?: boolean;
}

/**
 * Hi-Fi-aligned Artwork Details step body. Mounts `useArtworkDetailsForm`
 * and renders 8 design sections; the surrounding `<EditObjectStepShell>`
 * (Card + header + footer actions) is provided by `EditObject.tsx`. The
 * object identity (cover, title, catalog number) is shown in the sidebar,
 * so this step body starts straight at the form.
 */
export const DescribeStep: React.FC<DescribeStepProps> = ({
  catalogItemId,
  ownerId,
  isEditMode = false,
}) => {
  const form = useArtworkDetailsForm(catalogItemId, ownerId, isEditMode);
  const { setCatalogTitle } = useUpload();

  // Push live title edits to the upload context so the sidebar
  // (and anywhere else that reads `catalogTitle`) updates as the user types.
  useEffect(() => {
    if (form.isLoading) return;
    setCatalogTitle(form.title);
  }, [form.isLoading, form.title, setCatalogTitle]);

  return (
    <AccordionGroupProvider>
      {form.hasError ? (
        <div role="alert" style={{ color: 'var(--danger)' }}>
          Failed to load artwork details. Try refreshing the page.
        </div>
      ) : form.isLoading ? (
        <div style={{ color: 'var(--slate-500)', fontStyle: 'italic' }}>
          Loading artwork details…
        </div>
      ) : (
        <div data-testid="describe-step">
          <CreatorsSection form={form} />
          <TitleSection form={form} />
          <MediaDescriptionSection form={form} />
          <SignatureSection form={form} />
          <CreationDateSection form={form} />
          <MeasurementsSection form={form} />
          <KeywordsSection catalogItemId={catalogItemId} ownerId={ownerId} />
          <MultiplesEditionsSection form={form} />
          <AdditionalNotesSection form={form} />
        </div>
      )}
    </AccordionGroupProvider>
  );
};
